import { useState } from "react";
import { Globe, Plus, Trash2, UploadCloud } from "lucide-react";
import { api } from "@/data/api";
import { npmApiErrorMessage } from "@/data/npmState";
import { useAsync } from "@/hooks/useAsync";
import { Panel, AsyncBoundary, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

const CERT_ACTIONS = {
  kept: "certificado atual mantido",
  reused: "certificado existente reutilizado",
  requested: "novo certificado Let's Encrypt emitido",
};

// Domínio principal + aliases do MESMO Site. Registrar/remover alias não toca
// o NPM; "Aplicar ao NPM" atualiza o mesmo Proxy Host gerenciado.
export function SiteDomainsPanel({ siteId }) {
  const q = useAsync(() => api.getSiteDomains(siteId), [siteId]);
  const [view, setView] = useState(null);
  const [hostname, setHostname] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const data = view || q.data;

  const run = async (fn, success) => {
    setBusy(true);
    setMessage(null);
    try {
      const next = await fn();
      setView(next);
      setMessage({ ok: true, text: success(next) });
    } catch (e) {
      setMessage({ ok: false, text: npmApiErrorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const add = (event) => {
    event.preventDefault();
    run(() => api.addSiteAlias(siteId, hostname), (next) => {
      setHostname("");
      return next.pending ? "Alias registrado. Aplique ao NPM para publicá-lo." : "Alias registrado.";
    });
  };
  const remove = (alias) => run(() => api.removeSiteAlias(siteId, alias.id), () => `Alias ${alias.name} removido. Aplique ao NPM para retirá-lo do Proxy Host.`);
  const apply = () => run(() => api.applySiteDomains(siteId, accept), (next) => {
    const r = next.result || {};
    if (r.unchanged) return "O Proxy Host já estava com estes domínios — nenhuma alteração no NPM.";
    return `Domínios aplicados ao Proxy Host${r.certificate_action ? ` · ${CERT_ACTIONS[r.certificate_action] || r.certificate_action}` : ""}.`;
  });

  const host = data?.proxy_host;
  const needsTerms = !!host?.certificate_id;
  return (
    <Panel title="Domínios do Site" icon={Globe} className="lg:col-span-3" bodyClassName="space-y-4 text-sm">
      <AsyncBoundary loading={q.loading && !view} error={q.error}>
        {data && <>
          <div>
            <div className="text-xs text-muted-foreground">Domínio principal</div>
            <div className="mt-1 font-mono font-medium text-foreground" data-testid="site-primary-domain">{data.primary_domain || "—"}</div>
            {data.primary_needs_review && <div className="mt-2 space-y-1 rounded-lg border border-warn/30 bg-warn/10 p-2 text-xs" data-testid="site-primary-review">
              <p className="text-warn">NEEDS REVIEW: escolha o domínio principal; os demais viram aliases. Nada é alterado no NPM/DNS até "Aplicar ao NPM".</p>
              {data.unclassified.map((d) => <div key={d.id} className="flex items-center justify-between">
                <span className="font-mono text-foreground">{d.name}</span>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.setSitePrimary(siteId, d.name), () => `${d.name} definido como principal.`)}>Definir como principal</Button>
              </div>)}
            </div>}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Aliases / domínios adicionais</div>
            {data.aliases.length ? <ul className="mt-1 space-y-1" data-testid="site-aliases">
              {data.aliases.map((alias) => <li key={alias.id} className="flex items-center justify-between rounded-lg border border-line bg-white/[0.02] px-3 py-2">
                <span className="font-mono text-xs text-foreground">{alias.name}</span>
                <span className="flex items-center gap-2">
                  <StatusBadge status={alias.applied ? "healthy" : "warning"} label={alias.applied ? "Publicado" : "Pendente"} size="sm" pulse={false} />
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => remove(alias)} aria-label={`Remover ${alias.name}`}><Trash2 className="h-3.5 w-3.5" /> Remover</Button>
                </span>
              </li>)}
            </ul> : <p className="mt-1 text-xs text-muted-foreground">Nenhum alias. Ex.: www, loja ou outro domínio do mesmo site.</p>}
            <form className="mt-2 flex flex-wrap gap-2" onSubmit={add} data-testid="site-alias-form">
              <input name="hostname" value={hostname} onChange={(e) => setHostname(e.target.value)} required placeholder={data.primary_domain ? `www.${data.primary_domain}` : "www.exemplo.com.br"}
                className="min-w-0 flex-1 rounded border border-line bg-background px-2 py-1.5 font-mono text-xs" />
              <Button type="submit" size="sm" variant="outline" disabled={busy || !hostname.trim()}><Plus className="h-3.5 w-3.5" /> Adicionar alias</Button>
            </form>
          </div>
          <div className="rounded-lg border border-line px-3 py-2 text-xs">
            {!host ? <span className="text-muted-foreground">O Site ainda não possui Proxy Host. Crie-o em Nginx Proxy Manager.</span>
              : !host.managed ? <span className="text-muted-foreground">Proxy Host não gerenciado pela TintiCloud — aliases não são aplicados.</span>
              : <div className="space-y-2">
                <div className="text-muted-foreground">Proxy Host NPM #{host.npm_host_id}: <span className="font-mono text-foreground">{host.domain_names.join(", ")}</span>
                  {host.certificate_id ? ` · certificado #${host.certificate_id}` : " · sem SSL"}{host.drift_detected ? " · drift" : ""}</div>
                {data.pending && <div className="text-warn" data-testid="site-domains-pending">Pendente: o Proxy Host ainda não reflete os domínios do Site ({data.desired_domain_names.join(", ")}).</div>}
                {needsTerms && <label className="flex items-start gap-2"><input type="checkbox" name="accept_letsencrypt_terms" checked={accept} onChange={() => setAccept(!accept)} />
                  <span>Se o certificado atual não cobrir todos os domínios, emitir um novo via Let's Encrypt (concordo com os <a className="text-brand underline" href="https://letsencrypt.org/repository/" target="_blank" rel="noreferrer">termos</a>). Certificados compatíveis existentes são reutilizados.</span></label>}
                <Button size="sm" disabled={busy} onClick={apply} data-testid="site-domains-apply"><UploadCloud className="h-3.5 w-3.5" /> {busy ? "Aplicando…" : "Aplicar ao NPM"}</Button>
              </div>}
          </div>
          {message && <p role="alert" className={`rounded-lg border px-3 py-2 text-sm ${message.ok ? "border-ok/30 bg-ok/10 text-ok" : "border-err/30 bg-err/10 text-err"}`}>{message.text}</p>}
        </>}
      </AsyncBoundary>
    </Panel>
  );
}
